import React, {
  useRef,
  useCallback,
  useState,
  useMemo,
  useEffect,
} from 'react';
import { Form } from '@unform/web';
import { FaRegCopy, FaShareAlt } from 'react-icons/fa';
import { FormHandles } from '@unform/core';
import Notiflix from 'notiflix';
import { InsuredWallet } from './types';
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  FormGroup,
  Label,
  ButtonGroup,
  ActionButton,
  ShareModalTitle,
  ShareModalAttention,
  FullActionButton,
  SubtitleCentered,
  ShareCodeContainer,
  ShareCode,
  ShareActionButton,
  ShareActionButtons,
} from './CreateSharingModalStyles';
import {
  CheckboxGroup,
  LegalInformationModalText,
  StartSharingModalText,
} from './styles';
import Checkbox from '../../../components/Checkbox';

import Input from '../../../components/Input';
import cardSharingService, {
  CtaInfo,
} from '../../../services/cardSharingService';

import { formatCpf } from '../../../utils/formatt';
import theme from '../../../global/theme/light';

interface CreateSharingModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCard?: InsuredWallet | null;
  onRequestRefetch?: () => void;
}

interface CreateSharingModalFormData {
  cpf: string;
  name: string;
  acceptTerms: boolean;
}

const initialFormData: CreateSharingModalFormData = {
  cpf: '',
  name: '',
  acceptTerms: false,
};

// eslint-disable-next-line no-shadow
enum ModalScreenEnum {
  START,
  INTERNAL,
  EXTERNAL,
}

const CreateSharingModal: React.FC<CreateSharingModalProps> = ({
  isOpen,
  onClose,
  selectedCard,
  onRequestRefetch,
}) => {
  const [modalScreen, setModalScreen] = useState(ModalScreenEnum.START);
  const [shareInfo, setShareInfo] = useState<CreateSharingModalFormData | null>(
    null,
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [sharedToken, setSharedToken] = useState<string | null>(null);

  const [ctaInfo, setCtaInfo] = useState<CtaInfo | null>(null);

  const formRef = useRef<FormHandles>(null);

  useEffect(() => {
    cardSharingService.getCtaInfo().then((cta) => {
      setCtaInfo(cta?.content ?? null);
    });
  }, []);

  const handleOnClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleOnSubmit = useCallback(
    async (data: CreateSharingModalFormData) => {
      setIsSubmitting(true);
      Notiflix.Block.circle('.notiflix-create-sharing-button');

      try {
        formRef.current?.setErrors({});
        if (data.cpf.length < 11) {
          formRef.current?.setFieldError('cpf', 'CPF inválido');
        }

        if (!data.acceptTerms) {
          formRef.current?.setFieldError(
            'acceptTerms',
            'Você deve aceitar os termos',
          );
        }

        if (!data.name) {
          formRef.current?.setFieldError('name', 'Nome inválido');
        }

        if (
          Object.values(formRef.current?.getErrors() ?? {}).filter(Boolean)
            .length > 0
        ) {
          return;
        }

        setShareInfo(data);

        const userInfoFromCpf =
          await cardSharingService.getUnimedUserInfoFromCpf(data.cpf);

        if (!userInfoFromCpf.content || userInfoFromCpf.content.length === 0) {
          Notiflix.Notify.failure('Falha ao buscar informações do usuário');
          return;
        }

        switch (userInfoFromCpf.content?.[0].access) {
          case 'Externo': {
            const response = await cardSharingService.generateExternalSharing(
              data.cpf,
              data.name,
              // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain, @typescript-eslint/no-non-null-assertion
              selectedCard?.userUnimedId!,
            );

            if (!response.content || response.content.length === 0) {
              Notiflix.Notify.failure('Falha ao gerar compartilhamento');
              return;
            }

            setSharedToken(response.content[0].accessToken);
            setModalScreen(ModalScreenEnum.EXTERNAL);
            break;
          }
          case 'Interno': {
            await cardSharingService.generateInsuredSharing(
              // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain, @typescript-eslint/no-non-null-assertion
              selectedCard?.userUnimedId!,
              // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain, @typescript-eslint/no-non-null-assertion
              userInfoFromCpf.content[0].sharedUserUnimedId?.toString()!,
            );
            setModalScreen(ModalScreenEnum.INTERNAL);
            break;
          }
          default:
            Notiflix.Notify.failure('Falha ao buscar informações do usuário');
            return;
        }
      } finally {
        setIsSubmitting(false);
        Notiflix.Block.remove('.notiflix-create-sharing-button');
        onRequestRefetch?.();
      }
    },
    [selectedCard, onRequestRefetch],
  );

  const modalStep = useMemo(() => {
    switch (modalScreen) {
      case ModalScreenEnum.START: {
        return (
          <>
            <StartSharingModalText>
              Informe o CPF com qual deseja compartilhar o cartão virtual{' '}
              <strong>{selectedCard?.cardNumber}</strong> de{' '}
              {selectedCard?.insuredName}
            </StartSharingModalText>
            <Form
              onSubmit={handleOnSubmit}
              initialData={initialFormData}
              ref={formRef}
            >
              <FormGroup>
                <Label>CPF</Label>
                <Input
                  name="cpf"
                  type="text"
                  masks={['999.999.999-99']}
                  placeholder="000.000.000-00"
                  required
                />
              </FormGroup>

              <FormGroup>
                <Label>Nome</Label>
                <Input
                  name="name"
                  type="text"
                  placeholder="Luiz de Amaral"
                  required
                />
              </FormGroup>

              <LegalInformationModalText>
                É de responsabilidade do TITULAR do plano de saúde o
                compartilhamento e disseminação das informações pessoais
                contidas neste cartão virtual. O TITULAR dá anuência ao
                compartilhamento sob sua responsabilidade e conhece a LEI Nº
                13.709 que dispõe a proteção de dados pessoais.
              </LegalInformationModalText>

              <CheckboxGroup>
                <Checkbox name="acceptTerms" />
                <Label>Li e concordo com os termos acima</Label>
              </CheckboxGroup>

              <ButtonGroup>
                <ActionButton
                  type="button"
                  variant="secondary"
                  onClick={handleOnClose}
                >
                  Cancelar
                </ActionButton>
                <ActionButton
                  type="submit"
                  variant="primary"
                  disabled={isSubmitting}
                  className="notiflix-create-sharing-button"
                >
                  Criar Compartilhamento
                </ActionButton>
              </ButtonGroup>
            </Form>
          </>
        );
      }
      case ModalScreenEnum.EXTERNAL: {
        const cpf = formatCpf(shareInfo?.cpf ?? '');
        const accessToken = sharedToken!;

        const handleCopyCode = () => {
          window.navigator.clipboard.writeText(accessToken);
        };

        const handleShareCode = () => {
          if (!ctaInfo) return;

          const url = window.location.href;

          const text = ctaInfo.message
            .replaceAll('{{name}}', shareInfo?.name ?? '')
            .replaceAll('{{token}}', accessToken)
            .replaceAll('{{url}}', url);

          window.navigator.share({
            title: ctaInfo.title,
            text,
            url,
          });
        };

        return (
          <>
            <ShareModalTitle>
              O cartão foi compartilhado com o CPF: {cpf}
            </ShareModalTitle>
            <SubtitleCentered>Código de compartilhamento:</SubtitleCentered>
            <ShareCodeContainer>
              <ShareCode>{accessToken}</ShareCode>
              <ShareActionButtons>
                <ShareActionButton onClick={handleCopyCode}>
                  <FaRegCopy
                    size={20}
                    color={theme.colors.text.grayishToWhite}
                  />
                </ShareActionButton>
                {typeof window.navigator.share === 'function' && ctaInfo && (
                  <ShareActionButton onClick={handleShareCode}>
                    <FaShareAlt
                      size={20}
                      color={theme.colors.text.grayishToWhite}
                    />
                  </ShareActionButton>
                )}
              </ShareActionButtons>
            </ShareCodeContainer>
            <ShareModalAttention>Atenção</ShareModalAttention>
            <p>
              Oriente a pessoa com quem você compartilhou o cartão:
              <br />
              <br />
              1- <strong>Acessar</strong> o Portal da Unimed
              <br />
              2- Entrar na funcionalidade <strong>Cartão Virtual</strong>
              <br />
              3- Clicar no botão{' '}
              <strong>&quot;exibir cartão compartilhado&quot;</strong>
              <br />
              4- <strong>Digitar</strong> o código de compartilhamento.
            </p>
            <FullActionButton variant="primary" onClick={handleOnClose}>
              Concluir
            </FullActionButton>
          </>
        );
      }
      case ModalScreenEnum.INTERNAL: {
        const cpf = formatCpf(shareInfo?.cpf ?? '');
        return (
          <>
            <ShareModalTitle>
              O cartão foi compartilhado com o(a) beneficiário(a){' '}
              {shareInfo?.name} (CPF: {cpf})
            </ShareModalTitle>
            <ShareModalAttention>Atenção</ShareModalAttention>
            <p>
              Oriente a pessoa com quem você compartilhou o cartão:
              <br />
              <br />
              1- <strong>Acessar</strong> o Portal da Unimed
              <br />
              2- <strong>Fazer login no portal</strong>, caso estiver
              cadastrado, senão realizar o cadastro
              <br />
              3- Entrar na funcionalidade <strong>Cartão Virtual</strong>
            </p>
            <FullActionButton variant="primary" onClick={handleOnClose}>
              Concluir
            </FullActionButton>
          </>
        );
      }
      default:
        return null;
    }
  }, [
    modalScreen,
    selectedCard?.cardNumber,
    selectedCard?.insuredName,
    handleOnSubmit,
    handleOnClose,
    isSubmitting,
    shareInfo?.cpf,
    shareInfo?.name,
    sharedToken,
    ctaInfo,
  ]);

  if (!isOpen) return null;

  return (
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    <ModalOverlay onClick={() => {}}>
      <ModalContent onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Criar Compartilhamento</ModalTitle>
          <CloseButton onClick={handleOnClose}>&times;</CloseButton>
        </ModalHeader>

        <ModalBody>{modalStep}</ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

export default CreateSharingModal;
